import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updatePost } from "../../Services/Slices/postSlice";
import { fetchListPosts } from "../../Services/Slices/listPostsSlice";
import Loading from "../../Components/Loading/Loading";
import styles from "./Include.module.css";
import Button from "../../Components/Button/Button";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Autocomplete,
    TextField
} from "@mui/material";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import katex from "katex";
import "katex/dist/katex.min.css";
import Snackbar from "../../Components/Snackbar/Snackbar";
import {MdDriveFileRenameOutline} from "react-icons/md";
import {formatFileName} from "../../Components/Helper";
import { PATH } from "../../PATH";
import { Button as GetiButton } from "@geti/identidade-visual-react";

const editorOptions = {
    height: '720',
    buttonList: [
        ["undo", "redo"],
        ["removeFormat"],
        ["bold", "underline", "italic", "fontSize"],
        ["fontColor", "hiliteColor"],
        ["align", "horizontalRule", "list"],
        ["table", "link"],
        ["showBlocks", "codeView"],
        ["math"]
    ],
    katex: katex,
    imageRotation: false,
    inlineStyle: true,
    strictMode: false,
    fontSize: [12, 14, 16, 18, 20],
    colorList: [
        "#828282",
        "#FF5400",
        "#676464",
        "#F1F2F4",
        "#FF9B00",
        "#F00",
        "#fa6e30",
        "#000",
        "rgba(255, 153, 0, 0.1)",
        "#FF6600",
        "#0099FF",
        "#74CC6D",
        "#FF9900",
        "#CCCCCC"
    ]
};

const Include = () => {
    const contentRef = useRef<HTMLDivElement>();
    const [value, setValue] = useState("");
    const dispatch = useDispatch();
    const { fileName } = useParams();
    const url = `${PATH.baseApi}/render-html/?file_key=${fileName}`;

    const [htmlContent, setHtmlContent] = useState<string>("");
    const [editedHtml, setEditedHtml] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [headContent, setHeadContent] = useState("");
    const [styleContent, setStyleContent] = useState("");
    const [copySuccess, setCopySuccess] = useState(false);
    const [openMissingTextModal, setOpenMissingTextModal] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState<boolean>(true);

    const listPosts = useSelector((state: any) => state.listPostsSlice);

    useEffect(() => {
        if (!contentRef.current) return;
        contentRef.current.innerHTML = value;
    }, [value]);

    useEffect(() => {
        const fetchHtml = async () => {
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error("Erro! Tente novamente mais tarde.");
                }
                const html = await response.text();

                const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/);
                const headContent = headMatch ? headMatch[1] : "";
                const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/);
                const styleContent = styleMatch ? styleMatch[1] : "";
                const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);
                const bodyContent = bodyMatch ? bodyMatch[1] : "";

                setHtmlContent(bodyContent);
                setEditedHtml(bodyContent);
                setHeadContent(headContent);
                setStyleContent(styleContent);

                setLoading(false);
            } catch (err) {
                const errorMessage = (err as Error).message;
                setError(errorMessage);
                setLoading(false);
            }
        };

        fetchHtml();
    }, [url, fileName]);

    useEffect(() => {
        dispatch<any>(fetchListPosts("1", true));
    }, [dispatch]);

    const textToCopy = "INCLUÍDO POR: XXX Nº XXX DE XX/XX/XXXX";

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(textToCopy).then(
            () => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
                setShowSnackbar(true);
            },
            (err) => {
                setCopySuccess(false);
            }
        );
    };

    const handleSaveClick = () => {
        if (!editedHtml.includes("INCLUÍDO POR: XXX Nº XXX DE XX/XX/XXXX")) {
            handleCopyToClipboard();
            setOpenMissingTextModal(true);
        } else {
            setOpenDialog(true);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmDialogClose = () => {
        setOpenConfirmDialog(false);
    };

    const handleSubmit = () => {
        const fullHtml = `
            <html>
                <style>
                    ${styleContent}                
                </style>
                <head>
                    ${headContent}
                </head>
                <body>
                    ${editedHtml}
                </body>
            </html>
        `;

        const data = {
            file_name: fileName,
            old_text: htmlContent,
            include: fullHtml,
            edition: selectedFiles[0],
        };

        if (selectedFiles.length > 0) {
            (data as any).status = "Vigente Alterada";
        }

        dispatch<any>(updatePost(data));
        setOpenDialog(false);
        setOpenConfirmDialog(false);
    };

    const handleFormatClick = () => {
        setOpenConfirmDialog(true);
    };

    const onChangeHandler = (content: any) => {
        setEditedHtml(content);
    };

    if (loading) {
        return <Loading size="5rem" type="spin" />;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className={styles.container}>
            {showSnackbar && copySuccess && (
                <Snackbar
                setSnackbarType={setShowSnackbar} type="copyMessage"/>
            )}  
            <div className={styles.buttonContainer} id="buttons">
                <GetiButton variant="default" className={"mt-4"} onClick={handleSaveClick} id="linkButton">
                    Vincular Ato
                </GetiButton>
                <GetiButton variant="default" className={"mt-4"} onClick={handleFormatClick} id="formatButton">
                    Formatar
                </GetiButton>
                <GetiButton variant="default" className={"mt-4"} onClick={handleCopyToClipboard} id="copyButton">
                    Copiar Mensagem
                </GetiButton>
                <GetiButton variant="default" className={"mt-4"} onClick={() => window.location.href = `${PATH.base}/revogar-ato/${fileName}`}>
                    <MdDriveFileRenameOutline size={18} />
                    Modificar
                </GetiButton>
                <Autocomplete className={styles.navigate}
                    options={listPosts.data
                        .filter((file: any) => file.file_name.replace('/', '') !== fileName?.replace('/', ''))}
                    getOptionLabel={(file) => formatFileName(file.file_name)}
                    value={listPosts.data.find((file: any) => selectedFiles.includes(file.file_name)) || null}
                    onChange={(e, newValue) => {
                        window.location.href = `${PATH.base}/incluir/${newValue.file_name.replace('/', '')}`
                    }}
                    renderInput={(params) => <TextField {...params} label="Navegar" />}
                    noOptionsText="Nenhum Ato encontrado"
                    sx={{ width: 150 }}
                />
            </div>

            <div id="textEditor">
                <SunEditor
                    setOptions={editorOptions}
                    lang="pt_br"
                    setContents={editedHtml}
                    onChange={onChangeHandler}
                    />
            </div>

            <Dialog open={openMissingTextModal} onClose={() => setOpenMissingTextModal(false)}>
                <DialogTitle>Atenção!</DialogTitle>
                <DialogContent>
                    <p>Você precisa colar o texto "INCLUÍDO POR: XXX Nº XXX DE XX/XX/XXXX" no conteúdo inserido antes de finalizar.</p>
                    <p>Por favor, cole o texto que está em sua área de transferência no local onde foi feita a inclusão do novo texto.</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenMissingTextModal(false)}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Qual ato inclui esse trecho?</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        options={listPosts.data
                            .filter((file: any) => file.file_name.replace('/', '') !== fileName?.replace('/', ''))}
                        getOptionLabel={(file) => formatFileName(file.file_name)}
                        value={listPosts.data.find((file: any) => selectedFiles.includes(file.file_name)) || null}
                        onChange={(e, newValue) => {
                            if (newValue) {
                                setSelectedFiles([newValue.file_name]);
                            } else {
                                setSelectedFiles([]);
                            }
                        }}
                        renderInput={(params) => <TextField {...params} label="Escolha o ato responsável" />}
                        noOptionsText="Nenhum Ato encontrado"
                        sx={{ width: 300 }}
                    />
                </DialogContent>

                <DialogActions>
                    <Button className={styles.cancelButton} onClick={handleCloseDialog}>
                        Cancelar
                    </Button>
                    <Button className={styles.button} onClick={handleSubmit} disabled={selectedFiles.length === 0}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openConfirmDialog} onClose={handleConfirmDialogClose}>
                <DialogTitle>Você tem certeza? As alterações feitas serão salvas.</DialogTitle>
                <DialogActions>
                    <Button className={styles.cancelButton} onClick={handleConfirmDialogClose}>
                        Cancelar
                    </Button>
                    <Button className={styles.button} onClick={handleSubmit}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Include;
